import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/index.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { DISPLAY_LANGUAGES, useLocale } from '../contexts/LocaleContext.jsx';

function IconUpload() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M12 4v16m8-8H4" />
    </svg>
  );
}
function IconChat() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  );
}
function IconUser() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}
function IconHome() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  );
}
function IconSearch() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="m21 21-4.35-4.35m2.35-5.15a7.5 7.5 0 1 1-15 0 7.5 7.5 0 0 1 15 0Z" />
    </svg>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { locale, setLocale, t } = useLocale();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return;
    }

    let active = true;
    const loadUnreadCount = () => {
      api.messages.unreadCount()
        .then((data) => {
          if (active) setUnreadCount(data.count ?? 0);
        })
        .catch(() => {});
    };

    loadUnreadCount();
    const timer = setInterval(loadUnreadCount, 15000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [user]);

  function handleLogout() {
    logout();
    navigate('/');
  }

  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to={user ? '/feed' : '/'} className="flex items-center gap-2 text-brand-700 font-bold text-lg shrink-0">
          ⛳ Mahanakhon Golf
        </Link>

        {/* Nav links */}
        {user ? (
          <div className="flex items-center gap-1">
            <NavLink to="/feed" label={t('feed')} icon={<IconHome />} />
            <NavLink to="/caddies" label={t('caddies')} icon={<IconSearch />} />
            <NavLink to="/messages" label={t('messages')} icon={<IconChat />} badge={unreadCount} />
            {user.role === 'caddie' && (
              <NavLink to="/upload" label={t('post')} icon={<IconUpload />} />
            )}
            <NavLink to={`/${user.role === 'caddie' ? 'caddies' : 'golfers'}/${user.id}`} label={t('profile')} icon={<IconUser />} />
            <LanguageSelect locale={locale} setLocale={setLocale} label={t('displayLanguage')} />
            <button
              onClick={handleLogout}
              className="ml-2 text-sm text-gray-500 hover:text-gray-800 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {t('signOut')}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <LanguageSelect locale={locale} setLocale={setLocale} label={t('displayLanguage')} />
            <Link to="/signin" className="btn-secondary text-xs px-3 py-2">{t('signIn')}</Link>
            <Link to="/signup" className="btn-primary text-xs px-3 py-2">{t('signUp')}</Link>
          </div>
        )}
      </div>
    </nav>
  );
}

function NavLink({ to, label, icon, badge = 0 }) {
  return (
    <Link
      to={to}
      className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:text-brand-700 hover:bg-brand-50 transition-colors"
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
      {badge > 0 && (
        <span className="absolute -right-1 -top-1 min-w-4 rounded-full bg-red-500 px-1 text-center text-[10px] font-bold leading-4 text-white">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );
}

function LanguageSelect({ locale, setLocale, label }) {
  return (
    <select
      aria-label={label}
      value={locale}
      onChange={(event) => setLocale(event.target.value)}
      className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-600"
    >
      {DISPLAY_LANGUAGES.map(({ code, label: optionLabel }) => (
        <option key={code} value={code}>{optionLabel}</option>
      ))}
    </select>
  );
}
